import { types } from 'actions/organisation';
import { searchSuperOrganisationResearchers } from 'services/api';
import { buildQuery, noValue } from 'utils/search';
import { searchId as id } from 'components/common';
import { handleSearchError } from '../notifications';
import { getHasValidQuery } from './selectors';
import {
  applyFirstPickOnlyDefaults,
  applyTradeshowRules,
} from '../tradeshowFilterRules';
import { applyStateCountryRules } from '../geoFilterRules';

// People tab + the shared filter slice.
//
// SCIL-17744: `filters` is now shared between People and Activity. Every
// filter mutation flags BOTH tabs' changed-filters; each tab's PERFORM_*
// clears only its own flag when it runs. A still-set flag doubles as the
// "re-run this tab on activation" signal, so no separate staleness state.

// the counts are OpenSearch cardinalities, which are null over zero hits
const totalsFrom = res => ({
  people: res.distinctSuperResearchersCount ?? 0,
  emailCount: res.emailCount ?? 0,
  countries: res.countryCount ?? 0,
  activities:
    res.totalFunding +
    res.totalPosters +
    res.totalPublications +
    res.totalClinicalTrials,
});

export const researchersReducers = {
  [types.SET_RESEARCHERS_SEARCH_REQUEST]: (state, action) => {
    const { organisationId, searchRequest } = action.payload;

    if (action.payload.searchRequest?.error) {
      handleSearchError(action.payload.searchRequest.error);
    }
    if (searchRequest.res) {
      return {
        ...state,
        organisationResearchers: {
          ...state.organisationResearchers,
          [organisationId]: { ...searchRequest.res, retrievalDate: new Date() },
        },
        loading: { ...state.loading, [organisationId]: false },
        blockedForResearchersExport: {
          ...state.blockedForResearchersExport,
          [organisationId]: false,
        },
        totals: {
          ...state.totals,
          [organisationId]: totalsFrom(searchRequest.res),
        },
        researcherSearchError: {
          ...state.researcherSearchError,
          [organisationId]: action.payload.searchRequest?.error,
        },
      };
    }
    if (searchRequest.loading) {
      // Starting a search means the current filters are being applied, so the
      // stale/export block drops here, as it does for Activity and Clinical Trials.
      return {
        ...state,
        loading: { ...state.loading, [organisationId]: searchRequest.loading },
        blockedForResearchersExport: {
          ...state.blockedForResearchersExport,
          [organisationId]: false,
        },
        researcherSearchError: {
          ...state.researcherSearchError,
          [organisationId]: action.payload.searchRequest?.error,
        },
      };
    }

    return {
      ...state,
      loading: { ...state.loading, [organisationId]: false },
      researcherSearchError: {
        ...state.researcherSearchError,
        [organisationId]: action.payload.searchRequest?.error,
      },
      organisationResearchers: {},
      totals: {},
      blockedForResearchersExport: {
        ...state.blockedForResearchersExport,
        [organisationId]: false,
      },
    };
  },

  [types.SET_SORT]: (state, action) => {
    const { organisationId, value } = action.payload;
    return {
      ...state,
      sort: { ...state.sort, [organisationId]: value },
    };
  },

  [types.PERFORM_SEARCH]: (state, action) => {
    const { organisationId, request } = action.payload;
    if (!getHasValidQuery(state, organisationId)) {
      return {
        ...state,
        changedFilters: {
          ...state.changedFilters,
          [organisationId]: true,
        },
      };
    }

    const initialPageSize = 25;
    const initialPageNumber = 0;
    const initialOrder = 'LastActivityDate';
    const initialOrderBy = 'desc';
    const [query, , terms, nestedQueries] = buildQuery(
      state.filters[organisationId],
      null,
      null,
      true
    );

    const pageSize = state.pageSizes[organisationId]
      ? state.pageSizes[organisationId]
      : initialPageSize;

    const pageNumber = state.changedFilters[organisationId]
      ? initialPageNumber
      : state.pageNumbers[organisationId]
      ? state.pageNumbers[organisationId]
      : initialPageNumber;

    const order = state.sort[organisationId]
      ? state.sort[organisationId].field
      : initialOrder;

    const orderBy = state.sort[organisationId]
      ? state.sort[organisationId].direction
      : initialOrderBy;

    // SCIL-17603: mirror main search — carry the AI session id in the request
    // body under the wire key `sessionId` when the org slice holds one. It never
    // leaks into the Lucene query (aiFilterSessionId is in buildQuery skip keys).
    const aiSessionId = state.filters[organisationId]?.aiFilterSessionId;

    const cancelResearchersSearch = request({
      url: searchSuperOrganisationResearchers(organisationId),
      body: {
        id,
        query: `(${query})`,
        count: pageSize,
        from: pageSize * pageNumber,
        order,
        orderBy,
        filters: { ...terms },
        nestedQueries: nestedQueries,
        ...(aiSessionId ? { sessionId: aiSessionId } : {}),
      },
    });

    let changedFiltersState = { ...state.changedFilters };
    delete changedFiltersState[organisationId];
    return {
      ...state,
      pageNumbers: { ...state.pageNumbers, [organisationId]: pageNumber },
      pageSizes: { ...state.pageSizes, [organisationId]: pageSize },
      sort: {
        ...state.sort,
        [organisationId]: { field: order, direction: orderBy },
      },
      changedFilters: changedFiltersState,
      cancelResearchersSearch,
    };
  },

  [types.SET_PAGE_NUMBER]: (state, action) => {
    const { organisationId, pageNumber } = action.payload;
    return {
      ...state,
      pageNumbers: { ...state.pageNumbers, [organisationId]: pageNumber },
    };
  },
  [types.SET_PAGE_SIZE]: (state, action) => {
    const { organisationId, pageSize } = action.payload;
    return {
      ...state,
      pageSizes: { ...state.pageSizes, [organisationId]: pageSize },
      pageNumbers: { ...state.pageNumbers, [organisationId]: 0 },
    };
  },
  [types.SET_TOTALS]: (state, action) => {
    const { organisationId, totals } = action.payload;
    return {
      ...state,
      totals: { ...state.totals, [organisationId]: totals },
    };
  },

  // Back to a fresh page, as main search's Reset does; an absent date would blank the dropdown.
  [types.RESET_FILTERS]: (state, action) => {
    const { organisationId, defaultCategories } = action.payload;
    return {
      ...state,
      filters: {
        ...state.filters,
        [organisationId]: {
          workItemCategories: { ...defaultCategories },
          date: 'all',
        },
      },
      changedFilters: { ...state.changedFilters, [organisationId]: true },
      workItemChangedFilters: {
        ...state.workItemChangedFilters,
        [organisationId]: true,
      },
      blockedForResearchersExport: {
        ...state.blockedForResearchersExport,
        [organisationId]: true,
      },
      blockedForWorkItemExport: {
        ...state.blockedForWorkItemExport,
        [organisationId]: true,
      },
    };
  },

  // Matches main search's Clear; an absent date would blank the dropdown.
  [types.CLEAR_FILTERS]: (state, action) => {
    const { organisationId } = action.payload;
    return {
      ...state,
      filters: { ...state.filters, [organisationId]: { date: 'all' } },
      changedFilters: { ...state.changedFilters, [organisationId]: true },
      workItemChangedFilters: {
        ...state.workItemChangedFilters,
        [organisationId]: true,
      },
      blockedForResearchersExport: {
        ...state.blockedForResearchersExport,
        [organisationId]: true,
      },
      blockedForWorkItemExport: {
        ...state.blockedForWorkItemExport,
        [organisationId]: true,
      },
    };
  },

  [types.SET_EXHIBITOR_FILTER]: (state, action) => {
    return {
      ...state,
      filters: {
        ...state.filters,
        [action.payload.id]: action.payload.value,
      },
    };
  },

  [types.SET_FILTER_GROUP]: (state, action) => {
    const { organisationId, id, value } = action.payload;
    // Always produce a NEW org entry: the filter-count badges subscribe to
    // `filters[orgId]` by reference, so an in-place delete would leave them
    // showing stale counts. An emptied entry stays as `{}` — deleting it made
    // downstream `filters[orgId]` reads undefined.
    const orgFilters = { ...state.filters[organisationId] };
    if (noValue(value) && id !== 'term') {
      delete orgFilters[id];
    } else {
      orgFilters[id] = value;
    }
    const prevFilters = state.filters[organisationId] || {};
    applyTradeshowRules(prevFilters, orgFilters, id);
    applyFirstPickOnlyDefaults(prevFilters, orgFilters, id);
    applyStateCountryRules(prevFilters, orgFilters, id, 'country');

    return {
      ...state,
      filters: { ...state.filters, [organisationId]: orgFilters },
      changedFilters: { ...state.changedFilters, [organisationId]: true },
      workItemChangedFilters: {
        ...state.workItemChangedFilters,
        [organisationId]: true,
      },
      blockedForResearchersExport: {
        ...state.blockedForResearchersExport,
        [organisationId]: true,
      },
      blockedForWorkItemExport: {
        ...state.blockedForWorkItemExport,
        [organisationId]: true,
      },
    };
  },

  [types.SET_WORKITEM_CATEGORY_FILTER_GROUP]: (state, action) => {
    const { organisationId, id, value } = action.payload;
    // Copy entry and group before mutating — see SET_FILTER_GROUP.
    const nextGroup = {
      ...state.filters[organisationId]?.workItemCategories,
    };
    if (noValue(value)) {
      delete nextGroup[id];
    } else {
      nextGroup[id] = value;
    }

    return {
      ...state,
      filters: {
        ...state.filters,
        [organisationId]: {
          ...state.filters[organisationId],
          workItemCategories: nextGroup,
        },
      },
      changedFilters: { ...state.changedFilters, [organisationId]: true },
      workItemChangedFilters: {
        ...state.workItemChangedFilters,
        [organisationId]: true,
      },
      blockedForResearchersExport: {
        ...state.blockedForResearchersExport,
        [organisationId]: true,
      },
      blockedForWorkItemExport: {
        ...state.blockedForWorkItemExport,
        [organisationId]: true,
      },
    };
  },

  // SCIL-17770: nested `namedAuthor` group setter. buildQuery reads first/
  // middle/lastAuthor from a nested `namedAuthor` object, not flat keys, so the
  // three checkboxes fold into one group here — mirroring the workItemCategories
  // nested-group handler above.
  [types.SET_NAMED_AUTHOR_FILTER_GROUP]: (state, action) => {
    let newState = { ...state.filters };
    const { organisationId, id, value } = action.payload;
    if (newState[organisationId]?.namedAuthor === undefined) {
      newState[organisationId] = {
        ...newState[organisationId],
        namedAuthor: {},
      };
    }

    const nextGroup = { ...newState[organisationId].namedAuthor };
    if (noValue(value)) {
      delete nextGroup[id];
    } else {
      nextGroup[id] = value;
    }

    if (Object.keys(nextGroup).length === 0) {
      const { namedAuthor, ...withoutGroup } = newState[organisationId];
      newState[organisationId] = withoutGroup;
    } else {
      newState[organisationId] = {
        ...newState[organisationId],
        namedAuthor: nextGroup,
      };
    }

    return {
      ...state,
      filters: newState,
      changedFilters: { ...state.changedFilters, [organisationId]: true },
      workItemChangedFilters: {
        ...state.workItemChangedFilters,
        [organisationId]: true,
      },
      blockedForResearchersExport: {
        ...state.blockedForResearchersExport,
        [organisationId]: true,
      },
      blockedForWorkItemExport: {
        ...state.blockedForWorkItemExport,
        [organisationId]: true,
      },
    };
  },

  // Full reset for an AI apply: nothing survives but the date dropdown, which has no option for "unset".
  [types.FULL_RESET_FILTERS]: (state, action) => {
    const { organisationId } = action.payload;
    return {
      ...state,
      filters: { ...state.filters, [organisationId]: { date: 'all' } },
      changedFilters: { ...state.changedFilters, [organisationId]: true },
      workItemChangedFilters: {
        ...state.workItemChangedFilters,
        [organisationId]: true,
      },
      blockedForResearchersExport: {
        ...state.blockedForResearchersExport,
        [organisationId]: true,
      },
      blockedForWorkItemExport: {
        ...state.blockedForWorkItemExport,
        [organisationId]: true,
      },
    };
  },

  // SCIL-17603: atomic AI apply. The caller has already `fullResetFilters`'d the
  // slice, so this merges the mapped AI filters onto a clean slate and stamps the
  // session id together. Both changed flags are set: the active tab re-runs
  // immediately at the call site, the other refreshes on activation.
  [types.APPLY_AI_FILTERS]: (state, action) => {
    const { organisationId, filters, aiFilterSessionId } = action.payload;
    const merged = {
      ...state.filters[organisationId],
      ...filters,
      ...(aiFilterSessionId ? { aiFilterSessionId } : {}),
    };
    return {
      ...state,
      filters: { ...state.filters, [organisationId]: merged },
      changedFilters: { ...state.changedFilters, [organisationId]: true },
      workItemChangedFilters: {
        ...state.workItemChangedFilters,
        [organisationId]: true,
      },
      blockedForResearchersExport: {
        ...state.blockedForResearchersExport,
        [organisationId]: true,
      },
      blockedForWorkItemExport: {
        ...state.blockedForWorkItemExport,
        [organisationId]: true,
      },
    };
  },

  // SCIL-17603: set or clear ONLY the AI session id (baseline middleware passes
  // `undefined` to end the session). Deliberately does not touch filter values
  // or changed flags — ending a session must not restage a search.
  [types.SET_AI_FILTER_SESSION_ID]: (state, action) => {
    const { organisationId, aiFilterSessionId } = action.payload;
    const slice = { ...state.filters[organisationId] };
    if (aiFilterSessionId) {
      slice.aiFilterSessionId = aiFilterSessionId;
    } else {
      delete slice.aiFilterSessionId;
    }
    return {
      ...state,
      filters: { ...state.filters, [organisationId]: slice },
    };
  },

  [types.SET_ORG_RESEARCHER_FILTERS_BY_STATE_GROUP]: (state, action) => {
    const { organisationId, newState } = action.payload;
    return {
      ...state,
      filters: {
        ...state.filters,
        [organisationId]: newState,
      },
    };
  },
};
